import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Download, Search } from 'lucide-react';
import ReportHeader from '@/components/ReportHeader';
import { apiService } from '@/services/api';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';

const Container = styled.div`
  padding: 16px;
`;

const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const Acoes = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const Botao = styled.button`
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const Hint = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
`;

const ScrollTop = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  height: 16px;
  margin: 0 16px 8px 16px;
`;

const ScrollBottom = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`;

type LinhaRelatorio = {
  Filial: string;
  NC: string;
  DataVenda: string;
  Hora: string;
  Cliente: string;
  Vendedor: string;
  Aro: string;
  Pneus: string;
  ProdutosFaltantes: string;
};

const COLUNAS = ['Filial', 'NC', 'DataVenda', 'Hora', 'Cliente', 'Vendedor', 'Aro', 'Pneus', 'ProdutosFaltantes'] as const;

const LABELS: Record<string, string> = {
  Filial: 'Filial',
  NC: 'NC',
  DataVenda: 'Data da Venda',
  Hora: 'Hora',
  Cliente: 'Cliente',
  Vendedor: 'Vendedor',
  Aro: 'Aro',
  Pneus: 'Pneu(s)',
  ProdutosFaltantes: 'Produtos faltantes',
};

const formatarDataBR = (valor: string): string => {
  if (!valor) return '';
  const match = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(valor);
  return `${match[3]}/${match[2]}/${match[1]}`;
};

const RelatorioPneusSemObrigatorios: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const hoje = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [dataInicio, setDataInicio] = useState<string>(hoje);
  const [dataFim, setDataFim] = useState<string>(hoje);
  const [produtoInput, setProdutoInput] = useState<string>('');
  const [aroInput, setAroInput] = useState<string>('');
  const [dataInicioAplicada, setDataInicioAplicada] = useState<string>(hoje);
  const [dataFimAplicada, setDataFimAplicada] = useState<string>(hoje);
  const [produtoAplicado, setProdutoAplicado] = useState<string>('');
  const [aroAplicado, setAroAplicado] = useState<string>('');
  const [todasFiliais, setTodasFiliais] = useState<boolean>(false);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const [contentWidth, setContentWidth] = useState<number>(0);

  const filtrosProntos = todasFiliais || !!filialSelecionada?.id;

  const queryKey = useMemo(() => (
    [
      'relatorio-pneus-sem-obrigatorios',
      todasFiliais ? 'todas' : (filialSelecionada?.id || '-'),
      dataInicioAplicada,
      dataFimAplicada,
      produtoAplicado,
      aroAplicado,
    ]
  ), [todasFiliais, filialSelecionada?.id, dataInicioAplicada, dataFimAplicada, produtoAplicado, aroAplicado]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey,
    enabled: filtrosProntos,
    queryFn: async () => {
      const filtros = {
        dataInicio: dataInicioAplicada,
        dataFim: dataFimAplicada,
        produto: produtoAplicado.trim(),
        aro: aroAplicado.replace(/\D/g, ''),
      };
      if (todasFiliais) return apiService.obterPneusSemObrigatoriosTodas(filtros);
      if (!filialSelecionada?.id) return null;
      return apiService.obterPneusSemObrigatoriosFilial(filialSelecionada.id, filtros);
    }
  });

  const linhas = useMemo<LinhaRelatorio[]>(() => {
    return (data?.data?.dados || []) as LinhaRelatorio[];
  }, [data?.data?.dados]);

  const errosFiliais = useMemo<any[]>(() => {
    return (data?.data?.errosFiliais || []) as any[];
  }, [data?.data?.errosFiliais]);

  useEffect(() => {
    const bottom = bottomScrollRef.current;
    if (!bottom) return;
    const update = () => setContentWidth(bottom.scrollWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [linhas]);

  const handleTopScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && bottom.scrollLeft !== top.scrollLeft) {
      bottom.scrollLeft = top.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && top.scrollLeft !== bottom.scrollLeft) {
      top.scrollLeft = bottom.scrollLeft;
    }
  };

  const handleAplicar = () => {
    setDataInicioAplicada(dataInicio);
    setDataFimAplicada(dataFim);
    setProdutoAplicado(produtoInput.trim());
    setAroAplicado(aroInput.replace(/\D/g, ''));
  };

  const handleExportar = () => {
    if (linhas.length === 0) return;
    const sep = ';';
    const header = COLUNAS.map((coluna) => LABELS[coluna] || coluna).join(sep);
    const body = linhas.map((linha) => {
      return COLUNAS.map((coluna) => {
        const valor = coluna === 'DataVenda'
          ? formatarDataBR(String(linha[coluna] ?? ''))
          : String(linha[coluna] ?? '');
        const precisaAspas = valor.includes('"') || valor.includes(sep) || /[\n\r]/.test(valor);
        const escaped = valor.replace(/"/g, '""');
        return precisaAspas ? `"${escaped}"` : escaped;
      }).join(sep);
    }).join('\n');

    const contexto = todasFiliais ? 'todas' : (filialSelecionada?.nome?.replace(/[^\w\-\s]/g, '') || 'filial');
    const nomeArquivo = `pneus_sem_obrigatorios_${contexto}_${dataInicioAplicada}_a_${dataFimAplicada}.csv`;
    const csvContent = `\ufeff${header}\n${body}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Container>
      <ReportHeader
        title="Pneus sem produtos obrigatórios"
        subtitle={`${linhas.length} vendas retornadas no período de ${formatarDataBR(dataInicioAplicada)} a ${formatarDataBR(dataFimAplicada)}`}
        rightActions={(
          <Acoes>
            <Botao onClick={handleExportar} disabled={isLoading || isFetching || linhas.length === 0}>
              <Download size={16} /> Exportar
            </Botao>
            <Botao onClick={handleAplicar} disabled={isLoading || isFetching || (!todasFiliais && !filialSelecionada?.id)}>
              <Search size={16} /> Aplicar
            </Botao>
          </Acoes>
        )}
      />

      <Filtros>
        <Campo>
          <Label>Data Início</Label>
          <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
        </Campo>
        <Campo>
          <Label>Data Fim</Label>
          <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
        </Campo>
        <Campo>
          <Label>Produto</Label>
          <Input
            type="text"
            placeholder="Ex: 185/65R14"
            value={produtoInput}
            onChange={(e) => setProdutoInput(e.target.value)}
          />
          <Hint>Filtra pelo produto do pneu vendido.</Hint>
        </Campo>
        <Campo>
          <Label>Aro</Label>
          <Input
            type="text"
            placeholder="Ex: 14"
            value={aroInput}
            onChange={(e) => setAroInput(e.target.value.replace(/\D/g, ''))}
            style={{ minWidth: 120 }}
          />
        </Campo>
        <Campo>
          <Label>&nbsp;</Label>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={todasFiliais}
              onChange={(e) => setTodasFiliais(e.target.checked)}
            />
            Todas as Filiais
          </CheckboxRow>
        </Campo>
      </Filtros>

      {errosFiliais.length > 0 && (
        <div style={{ padding: '0 16px 12px 16px', color: '#92400e', fontSize: 13 }}>
          Algumas filiais não responderam: {errosFiliais.map((e) => e.filialNome || e.filialId).join(', ')}
        </div>
      )}

      <ScrollTop ref={topScrollRef} onScroll={handleTopScroll}>
        <div style={{ width: contentWidth }} />
      </ScrollTop>

      <ScrollBottom ref={bottomScrollRef} onScroll={handleBottomScroll}>
        <Tabela>
          <thead>
            <tr>
              {COLUNAS.map((coluna) => (
                <Th key={coluna}>{LABELS[coluna] || coluna}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><Td colSpan={COLUNAS.length}>Carregando...</Td></tr>
            ) : linhas.length === 0 ? (
              <tr><Td colSpan={COLUNAS.length}>Nenhum registro encontrado</Td></tr>
            ) : (
              linhas.map((linha, idx) => (
                <tr key={`${linha.Filial}-${linha.NC}-${linha.Aro}-${idx}`}>
                  <Td>{linha.Filial}</Td>
                  <Td>{linha.NC}</Td>
                  <Td>{formatarDataBR(linha.DataVenda)}</Td>
                  <Td>{linha.Hora}</Td>
                  <Td>{linha.Cliente}</Td>
                  <Td>{linha.Vendedor}</Td>
                  <Td>{linha.Aro}</Td>
                  <Td>{linha.Pneus}</Td>
                  <Td>{linha.ProdutosFaltantes}</Td>
                </tr>
              ))
            )}
          </tbody>
        </Tabela>
      </ScrollBottom>
    </Container>
  );
};

export default RelatorioPneusSemObrigatorios;
